/**
 * OverlayDeviationViewer — Phase 4 + 5.
 *
 * Renders:
 *   - Forge Viewer with the design model
 *   - A Three.js Points overlay (the scan/deviation cloud) injected as a Forge
 *     overlay scene, sharing Forge's camera so panning/orbiting stays in sync
 *   - Deviation Legend chip
 *   - Deviation Zones table with fly-to, raise-RAID, attach-photo, severity badge
 *   - Approval workflow buttons (submit / approve / reject)
 *   - Export PDF / PPTX
 *
 * Deviation buffer is fetched from /viewer/deviation-points (Nx7 float32:
 * x,y,z,r,g,b,a) and downsampled to keep the in-browser footprint manageable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import api from '@/lib/api';
import { toast } from 'sonner';
import ForgeViewerWrapper from '@/components/scan3d/ForgeViewerWrapper';
import DeviationLegend from '@/components/scan3d/DeviationLegend';
import PointInspectionTooltip from '@/components/scan3d/PointInspectionTooltip';
import CameraBookmarksPanel from '@/components/scan3d/CameraBookmarksPanel';
import {
  Play, Loader2, Layers, Target, AlertCircle, Camera, ShieldCheck, ShieldOff,
  FileDown, Send, Check, X, Crosshair, Plus, Scissors, Ruler, ExternalLink,
} from 'lucide-react';

const SEV_COLOR = { CRITICAL: '#ef4444', MAJOR: '#f97316', MINOR: '#eab308', WITHIN_TOLERANCE: '#10b981' };

export default function OverlayDeviationViewer({ projectId }) {
  // Selection / lists
  const [models, setModels] = useState([]);
  const [scans, setScans] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [modelId, setModelId] = useState('');
  const [scanId, setScanId] = useState('');
  // Composite scans (multi-scan comparison) — when populated (length≥1), the
  // create call sends `scanSessionIds` instead of a single `scanSessionId`.
  const [extraScanIds, setExtraScanIds] = useState([]);
  const [composite, setComposite] = useState(false);
  const [comparisonId, setComparisonId] = useState('');
  const [tolerance, setTolerance] = useState(25);
  const [busy, setBusy] = useState(false);

  // Resolved
  const [model, setModel] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [zones, setZones] = useState([]);
  const [stats, setStats] = useState(null);

  // Viewer state
  const viewerRef = useRef(null);
  const overlayRef = useRef(null);
  const rawBufferRef = useRef(null);          // raw Nx7 Float32Array from server
  const deviationMagsRef = useRef(null);      // |deviation| per point (precomputed once)
  const cuttingPlanesRef = useRef([]);
  const [overlayInfo, setOverlayInfo] = useState({ pointCount: 0, bytes: 0 });
  const [pointSize, setPointSize] = useState(4);
  const [opacity, setOpacity] = useState(0.85);
  const [liveTolerance, setLiveTolerance] = useState(25);  // independent of server-side tolerance
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [inspectData, setInspectData] = useState(null);    // {deviationMm, x, y, z, screenX, screenY, component, zoneId}
  const [sectionPlaneOn, setSectionPlaneOn] = useState(false);
  const [measureOn, setMeasureOn] = useState(false);
  // Mesh-readiness state — when the backend signals MESH_NOT_READY we surface
  // a status pill and poll until OBJ is ready (or the user cancels).
  const [meshStatus, setMeshStatus] = useState(null); // {state: 'PREPARING'|'TIMEOUT'|'READY', startedAt}

  // Lists
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      const [m, s, c] = await Promise.all([
        api.get(`/v1/projects/${projectId}/scan3d/models?isLatest=true`),
        api.get(`/v1/projects/${projectId}/scan3d/sessions?limit=50`),
        api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons?limit=50`),
      ]);
      setModels((m.data?.models || []).filter(x => x.translationStatus === 'SUCCESS'));
      setScans(s.data?.sessions || s.data?.items || []);
      setComparisons(c.data?.comparisons || []);
    })().catch(() => toast.error('Lists load failed'));
  }, [projectId]);

  // Resolve model
  useEffect(() => {
    if (!modelId) { setModel(null); return; }
    api.get(`/v1/projects/${projectId}/scan3d/models/${modelId}`).then(r => setModel(r.data));
  }, [modelId, projectId]);

  // Resolve comparison
  const refreshComparison = useCallback(async () => {
    if (!comparisonId) { setComparison(null); setZones([]); setStats(null); return; }
    try {
      const [c, z] = await Promise.all([
        api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}`),
        api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/zones`),
      ]);
      setComparison(c.data);
      setZones(z.data?.zones || []);
      setStats(z.data?.statistics || null);
      // Auto-pick the comparison's model so Forge loads
      const cmpModelId = c.data?.designModelId;
      if (cmpModelId && !modelId) setModelId(cmpModelId);
    } catch (e) { toast.error('Failed to load comparison'); }
  }, [projectId, comparisonId, modelId]);

  useEffect(() => { refreshComparison(); }, [refreshComparison]);

  // Build the deviation overlay whenever (viewer, comparison) are both ready
  const loadOverlay = useCallback(async () => {
    if (!viewerRef.current || !comparisonId) return;
    try {
      const r = await api.get(
        `/v1/projects/${projectId}/scan3d/viewer/deviation-points/${comparisonId}?downsample=4`,
        { responseType: 'arraybuffer' }
      );
      const data = new Float32Array(r.data);
      rawBufferRef.current = data;
      const n = Math.floor(data.length / 7);
      if (!n) return;
      const positions = new Float32Array(n * 3);
      const colors = new Float32Array(n * 4);
      // Compute |deviation| per point from the RGBA encoding (used for live re-coloring)
      // We approximate magnitude bucket from RGB; for true magnitude we'd need another channel.
      const mags = new Float32Array(n);
      const serverTol = (comparison?.deviationData?.toleranceThresholdMm) || 25;
      for (let i = 0; i < n; i++) {
        positions[i * 3] = data[i * 7];
        positions[i * 3 + 1] = data[i * 7 + 1];
        positions[i * 3 + 2] = data[i * 7 + 2];
        const cr = data[i * 7 + 3];
        const cg = data[i * 7 + 4];
        const cb = data[i * 7 + 5];
        const ca = data[i * 7 + 6];
        colors[i * 4] = cr;
        colors[i * 4 + 1] = cg;
        colors[i * 4 + 2] = cb;
        colors[i * 4 + 3] = ca;
        // Recover the server's bucket → mm range estimate (use the bucket midpoint as proxy)
        let ratio;
        if (cr < 0.05 && cg > 0.5) ratio = 0.1;       // green
        else if (cr > 0.8 && cg > 0.8) ratio = 0.4;   // yellow
        else if (cr > 0.9 && cg > 0.3 && cg < 0.7) ratio = 0.8; // orange
        else ratio = 1.5;                              // red (above tolerance)
        mags[i] = ratio * serverTol;
      }
      deviationMagsRef.current = mags;

      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geom.setAttribute('color', new THREE.BufferAttribute(colors, 4));
      const mat = new THREE.PointsMaterial({
        size: pointSize,
        sizeAttenuation: false,
        vertexColors: true,
        transparent: true,
        opacity,
        depthWrite: false,
      });
      const cloud = new THREE.Points(geom, mat);
      const al = model?.alignmentTransform;
      if (al?.isAligned) {
        cloud.position.set(al.translation.x, al.translation.y, al.translation.z);
        cloud.rotation.set(
          (al.rotation.x * Math.PI) / 180,
          (al.rotation.y * Math.PI) / 180,
          (al.rotation.z * Math.PI) / 180
        );
        cloud.scale.setScalar(al.scale || 1.0);
      }
      try {
        if (overlayRef.current) viewerRef.current.impl.removeOverlay('scanDeviation', overlayRef.current);
      } catch (_) { /* ignore */ }
      overlayRef.current = cloud;
      try {
        viewerRef.current.impl.createOverlayScene('scanDeviation');
      } catch (_) { /* already exists */ }
      viewerRef.current.impl.addOverlay('scanDeviation', cloud);
      viewerRef.current.impl.invalidate(true, true, true);
      setOverlayInfo({ pointCount: n, bytes: r.data.byteLength });
      setLiveTolerance(serverTol);
    } catch (e) {
      toast.error(`Overlay load failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [projectId, comparisonId, model, pointSize, opacity, comparison]);

  // Live tolerance recolor (no server roundtrip)
  const recolorOverlay = useCallback((newTol) => {
    const cloud = overlayRef.current;
    const mags = deviationMagsRef.current;
    if (!cloud || !mags) return;
    const colors = cloud.geometry.attributes.color.array;
    for (let i = 0; i < mags.length; i++) {
      const ratio = Math.abs(mags[i]) / Math.max(newTol, 1e-6);
      let r, g, b, a;
      if (ratio <= 0.2)        { r = 0; g = 0.8; b = 0; a = 0.6; }
      else if (ratio <= 0.6)   { r = 0.9; g = 0.9; b = 0; a = 0.7; }
      else if (ratio <= 1.0)   { r = 1; g = 0.5; b = 0; a = 0.8; }
      else                     { r = 1; g = 0; b = 0; a = 1.0; }
      colors[i * 4] = r;
      colors[i * 4 + 1] = g;
      colors[i * 4 + 2] = b;
      colors[i * 4 + 3] = a;
    }
    cloud.geometry.attributes.color.needsUpdate = true;
    if (viewerRef.current) viewerRef.current.impl.invalidate(true, true, true);
  }, []);

  useEffect(() => { recolorOverlay(liveTolerance); }, [liveTolerance, recolorOverlay]);

  // WebSocket — listen for OBJ-ready pushes from the backend poller so the
  // "Preparing mesh" banner clears + the execute call retries the moment APS
  // finishes the OBJ derivative. Falls back to the 60s setTimeout in runExecute
  // if the WS is unavailable.
  useEffect(() => {
    if (!projectId) return undefined;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = base.replace(/^http/, 'ws') + `/api/scan3d/jobs/ws?projectId=${projectId}`;
    let ws;
    let reconnectTimer;
    let stopped = false;
    const connect = () => {
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            if (msg.kind === 'objStatus' && msg.objMeshReady) {
              setMeshStatus(prev => {
                if (prev?.state === 'PREPARING' && prev.cmpId) {
                  // Re-run execute immediately — real point-to-mesh is now available
                  setTimeout(() => runExecute(prev.cmpId, false), 250);
                }
                return null;
              });
              toast.success('Mesh ready — retrying real point-to-mesh deviation');
            }
          } catch (_) { /* ignore */ }
        };
        ws.onclose = () => { if (!stopped) reconnectTimer = setTimeout(connect, 5000); };
      } catch (_) { if (!stopped) reconnectTimer = setTimeout(connect, 5000); }
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      if (ws) { ws.onclose = null; try { ws.close(); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Click-on-point inspection + design model component selection
  useEffect(() => {
    const v = viewerRef.current;
    if (!v || !overlayRef.current) return;
    const handleClick = (e) => {
      try {
        const rect = v.container.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        // 1) Raycast against the point cloud overlay
        const cam = v.impl.camera;
        const raycaster = new THREE.Raycaster();
        raycaster.params.Points = { threshold: pointSize * 2 };
        raycaster.setFromCamera({ x, y }, cam);
        const hits = raycaster.intersectObject(overlayRef.current);
        if (hits && hits.length > 0) {
          const hit = hits[0];
          const idx = hit.index;
          const mag = deviationMagsRef.current ? deviationMagsRef.current[idx] : null;
          // 2) Hit-test the Forge model surface at the same pixel for component name
          let component = null;
          try {
            const fhit = v.impl.hitTest(e.clientX - rect.left, e.clientY - rect.top, false);
            if (fhit && fhit.dbId) {
              v.select([fhit.dbId]);  // highlight component
              v.getProperties(fhit.dbId, (props) => {
                component = props?.name || `dbId ${fhit.dbId}`;
                setInspectData(prev => prev ? { ...prev, component } : prev);
              });
            }
          } catch (_) { /* ignore */ }
          setInspectData({
            screenX: e.clientX,
            screenY: e.clientY,
            x: hit.point.x,
            y: hit.point.y,
            z: hit.point.z,
            deviationMm: mag,
            severity: mag && Math.abs(mag) > liveTolerance * 2 ? 'CRITICAL' :
                      mag && Math.abs(mag) > liveTolerance ? 'MAJOR' :
                      mag && Math.abs(mag) > liveTolerance * 0.6 ? 'MINOR' : 'WITHIN_TOLERANCE',
            direction: mag > 0 ? 'PROTRUSION' : 'RECESSION',
            component,
          });
        }
      } catch (_) { /* ignore */ }
    };
    v.container.addEventListener('click', handleClick);
    return () => v.container.removeEventListener('click', handleClick);
  }, [viewerRef.current, overlayRef.current, pointSize, liveTolerance]);

  useEffect(() => { loadOverlay(); }, [loadOverlay]);

  // Create + execute (handles MESH_NOT_READY by polling the model's OBJ status)
  const createAndExecute = async () => {
    if (!modelId || !scanId) { toast.error('Pick model + scan'); return; }
    setBusy(true);
    try {
      const allScans = composite ? [scanId, ...extraScanIds.filter(s => s && s !== scanId)] : [scanId];
      const r = await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons`, {
        scanSessionId: scanId,
        ...(composite && allScans.length > 1 ? { scanSessionIds: allScans } : {}),
        designModelId: modelId, toleranceThresholdMm: tolerance, computeMethod: 'CLOUD_TO_MESH',
      });
      const cmp = r.data;
      setComparisonId(cmp.id);
      await runExecute(cmp.id, /*allowSynthetic*/ false);
      const c2 = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons?limit=50`);
      setComparisons(c2.data?.comparisons || []);
    } catch (e) {
      toast.error(`Execute failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runExecute = async (cmpId, allowSynthetic = false) => {
    try {
      const qs = allowSynthetic ? '?allowSynthetic=true' : '';
      const exec = await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${cmpId}/execute${qs}`);
      setMeshStatus(null);
      toast.success(`Executed — ${exec.data.zoneCount} zones, ${exec.data.raisedCount} RAID raised`);
      refreshComparison();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Backend returns 409 with structured detail when OBJ derivative is not ready
      if (e?.response?.status === 409 && (detail?.code === 'MESH_NOT_READY' || /MESH_NOT_READY|OBJ derivative/.test(detail || ''))) {
        const objStat = detail?.objExportStatus || 'REQUESTED';
        setMeshStatus({ state: 'PREPARING', objStatus: objStat, startedAt: Date.now(), cmpId });
        toast.message('Preparing mesh — model export in progress. Will auto-retry in 60s.', { duration: 6000 });
        // Schedule a retry sweep
        setTimeout(() => runExecute(cmpId, false), 60_000);
      } else {
        toast.error(`Execute failed: ${detail?.message || detail || e.message}`);
      }
    }
  };

  // Fly-to zone — also dims the rest of the overlay so the zone "pops"
  const flyTo = (zone) => {
    if (!viewerRef.current) return;
    try {
      const c = zone.centroid;
      const bb = zone.boundingBox;
      const size = Math.max(
        bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z, 1000
      );
      const dist = size * 2.5;
      const nav = viewerRef.current.navigation;
      nav.setTarget({ x: c.x, y: c.y, z: c.z });
      nav.setPosition({ x: c.x + dist, y: c.y + dist, z: c.z + dist * 0.6 });
      setSelectedZoneId(zone.zoneId);
      highlightZone(zone);
      toast.success(`Flying to ${zone.zoneId}`);
    } catch (_) { /* ignore */ }
  };

  // Dim every point not inside the zone's bbox; bright points in zone
  const highlightZone = (zone) => {
    const cloud = overlayRef.current;
    if (!cloud) return;
    const pos = cloud.geometry.attributes.position.array;
    const col = cloud.geometry.attributes.color.array;
    const n = Math.floor(pos.length / 3);
    const bb = zone.boundingBox;
    for (let i = 0; i < n; i++) {
      const x = pos[i * 3];
      const y = pos[i * 3 + 1];
      const z = pos[i * 3 + 2];
      const inside = (x >= bb.min.x && x <= bb.max.x &&
                      y >= bb.min.y && y <= bb.max.y &&
                      z >= bb.min.z && z <= bb.max.z);
      col[i * 4 + 3] = inside ? 1.0 : 0.10;
    }
    cloud.geometry.attributes.color.needsUpdate = true;
    if (viewerRef.current) viewerRef.current.impl.invalidate(true, true, true);
  };

  const clearHighlight = () => {
    setSelectedZoneId(null);
    recolorOverlay(liveTolerance);  // reset alpha by re-running the colour mapping
  };

  const toggleSectionPlane = async () => {
    const v = viewerRef.current;
    if (!v) return;
    try {
      // Prefer Forge's interactive Section extension (handles plane + box + drag handles).
      // Fall back to the legacy single-plane cut if the extension fails to load.
      if (!sectionPlaneOn) {
        let ext = v.getExtension('Autodesk.Section');
        if (!ext) ext = await v.loadExtension('Autodesk.Section');
        if (ext) {
          ext.activate('Autodesk.Section.Plane') || ext.activate();
        } else {
          const bb = model?.modelMetadata?.boundingBox;
          const midZ = bb ? (bb.min.z + bb.max.z) / 2 : 1000;
          const plane = new THREE.Vector4(0, 0, -1, midZ);
          v.setCutPlanes([plane]);
          cuttingPlanesRef.current = [plane];
        }
        setSectionPlaneOn(true);
      } else {
        const ext = v.getExtension('Autodesk.Section');
        if (ext) ext.deactivate();
        v.setCutPlanes([]);
        cuttingPlanesRef.current = [];
        setSectionPlaneOn(false);
      }
    } catch (e) { toast.error('Section toggle failed'); }
  };

  const toggleMeasure = async () => {
    const v = viewerRef.current;
    if (!v) return;
    try {
      if (!measureOn) {
        let ext = v.getExtension('Autodesk.Measure');
        if (!ext) ext = await v.loadExtension('Autodesk.Measure');
        if (!ext) { toast.error('Measure extension unavailable'); return; }
        // Default to distance; user can switch to angle/area inside the extension UI.
        try { ext.activate('distance'); } catch { ext.activate(); }
        setMeasureOn(true);
      } else {
        const ext = v.getExtension('Autodesk.Measure');
        if (ext) ext.deactivate();
        setMeasureOn(false);
      }
    } catch (e) { toast.error('Measure toggle failed'); }
  };

  const raiseRAID = async (zone) => {
    try {
      const r = await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/zones/${zone.zoneId}/raise-raid`);
      toast.success(r.data.alreadyExists ? 'RAID already exists' : `RAID ${r.data.raidItemId} raised`);
      refreshComparison();
    } catch (e) { toast.error('Raise failed'); }
  };

  const attachPhoto = async (zone, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/zones/${zone.zoneId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo attached');
      refreshComparison();
    } catch (e) { toast.error('Photo upload failed'); }
  };

  const exportReport = async (fmt) => {
    if (!comparisonId) return;
    try {
      const r = await api.get(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/export-report?format=${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `deviation_${comparison?.comparisonCode || comparisonId}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const submitForApproval = async () => {
    if (!comparisonId) return;
    await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/submit-approval`, {});
    toast.success('Submitted for QA/QC approval');
    refreshComparison();
  };
  const approve = async () => {
    await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/approve`, { comment: 'Reviewed and approved' });
    toast.success('Approved');
    refreshComparison();
  };
  const reject = async () => {
    const reason = window.prompt('Rejection reason?', '');
    if (reason == null) return;
    await api.post(`/v1/projects/${projectId}/scan3d/deviation-comparisons/${comparisonId}/reject`, { reason });
    toast.info('Rejected');
    refreshComparison();
  };

  return (
    <div className="space-y-3" data-testid="scan3d-overlay-tab">
      {/* Control bar */}
      <div className="rounded-xl p-3 flex flex-wrap items-end gap-2" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
        <label className="text-xs">
          <span className="opacity-70">Comparison</span>
          <select value={comparisonId} onChange={e => setComparisonId(e.target.value)}
            className="block mt-1 px-2 py-1 rounded min-w-[200px]" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
            data-testid="overlay-comparison-select">
            <option value="">— new or pick existing —</option>
            {comparisons.map(c => <option key={c.id} value={c.id}>{c.comparisonCode} · {c.title} · {c.status}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="opacity-70">Scan</span>
          <select value={scanId} onChange={e => setScanId(e.target.value)}
            className="block mt-1 px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}>
            <option value="">— pick scan —</option>
            {scans.map(s => <option key={s.id || s.sessionCode} value={s.id || s.sessionCode}>{s.sessionCode}</option>)}
          </select>
        </label>
        <button onClick={() => setComposite(c => !c)}
                className="text-[10px] px-2 py-1 rounded font-semibold self-end"
                style={{
                  background: composite ? '#06b6d422' : '#0a0e14',
                  color: composite ? '#06b6d4' : '#9ca3af',
                  border: '1px solid #1f2937',
                }}
                title="Compose multiple scan sessions into one comparison"
                data-testid="overlay-composite-toggle">
          {composite ? '✓ Composite ON' : '+ Composite'}
        </button>
        {composite && extraScanIds.map((sid, idx) => (
          <label key={`extra-${idx}`} className="text-xs" data-testid={`overlay-extra-scan-${idx}`}>
            <span className="opacity-70">+ Scan {idx + 2}</span>
            <div className="flex items-center gap-1 mt-1">
              <select value={sid}
                onChange={e => setExtraScanIds(prev => prev.map((x, i) => i === idx ? e.target.value : x))}
                className="px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}>
                <option value="">— pick scan —</option>
                {scans.map(s => <option key={s.id || s.sessionCode} value={s.id || s.sessionCode}>{s.sessionCode}</option>)}
              </select>
              <button onClick={() => setExtraScanIds(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1 rounded text-red-400 hover:bg-red-900/30" aria-label="Remove">
                <X className="w-3 h-3" />
              </button>
            </div>
          </label>
        ))}
        {composite && (
          <button onClick={() => setExtraScanIds(prev => [...prev, ''])}
                  className="text-[10px] px-2 py-1 rounded self-end"
                  style={{ background: '#0a0e14', color: '#06b6d4', border: '1px dashed #06b6d466' }}
                  data-testid="overlay-add-extra-scan-btn">
            + Add another scan
          </button>
        )}
        <label className="text-xs">
          <span className="opacity-70">Model</span>
          <select value={modelId} onChange={e => setModelId(e.target.value)}
            className="block mt-1 px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}>
            <option value="">— pick model —</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.modelCode} · {m.modelName}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="opacity-70">Tolerance (mm)</span>
          <input type="number" min={1} step={1} value={tolerance} onChange={e => setTolerance(parseFloat(e.target.value) || 25)}
            className="block mt-1 w-24 px-2 py-1 rounded" style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }} />
        </label>
        <button onClick={createAndExecute} disabled={busy || !modelId || !scanId}
          className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold disabled:opacity-40"
          style={{ background: '#10b981', color: '#fff' }}
          data-testid="overlay-execute-btn">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          Create & Execute
        </button>
      </div>

      {/* Viewer + stats */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-3">
        <div className="xl:col-span-8 space-y-2">
          {meshStatus?.state === 'PREPARING' && (
            <div className="rounded-lg px-3 py-2 flex items-center gap-2 text-[11px]"
              style={{ background: '#f59e0b15', border: '1px solid #f59e0b66', color: '#f59e0b' }}
              data-testid="overlay-mesh-preparing">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="font-semibold">Preparing mesh</span>
              <span className="opacity-80">
                Model export in progress for real point-to-mesh deviation
                (status: {meshStatus.objStatus}). Will auto-retry in 60s.
              </span>
              <button onClick={() => runExecute(meshStatus.cmpId, /*allowSynthetic*/ true)}
                className="ml-auto px-2 py-0.5 rounded text-[10px]"
                style={{ background: '#f59e0b', color: '#000' }}
                data-testid="overlay-allow-synthetic-btn">
                Run synthetic now
              </button>
              <button onClick={() => setMeshStatus(null)}
                className="px-2 py-0.5 rounded text-[10px]"
                style={{ background: '#0a0e14', color: '#f59e0b', border: '1px solid #f59e0b66' }}>
                Dismiss
              </button>
            </div>
          )}
          {model ? (
            <ForgeViewerWrapper projectId={projectId} urn={model.svf2Urn || model.apsUrn}
              onReady={(v) => { viewerRef.current = v; loadOverlay(); }}
              height={560} testId="overlay-forge-viewer" />
          ) : (
            <div className="h-[560px] rounded-xl flex items-center justify-center text-sm opacity-60"
              style={{ background: '#0a0e14', border: '1px solid #1f2937' }}>Pick a design model</div>
          )}
          <DeviationLegend toleranceMm={liveTolerance} />
          {overlayInfo.pointCount > 0 && (
            <div className="text-[10px] opacity-50 font-mono" data-testid="overlay-info">
              Overlay: {overlayInfo.pointCount.toLocaleString()} points · {(overlayInfo.bytes / 1024).toFixed(0)} KB
              {comparison?.deviationData?.engineMode &&
                <span className="ml-2 px-1.5 py-0.5 rounded uppercase tracking-wider"
                  style={{ background: comparison.deviationData.engineMode === 'synthetic' ? '#f59e0b22' : '#10b98122',
                           color: comparison.deviationData.engineMode === 'synthetic' ? '#f59e0b' : '#10b981' }}>
                  Engine: {comparison.deviationData.engineMode}
                </span>}
            </div>
          )}
          {/* Overlay controls */}
          <div className="flex flex-wrap items-center gap-3 text-[11px] px-3 py-2 rounded-lg" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
            <label className="flex items-center gap-1.5">
              <span className="opacity-60">Point size</span>
              <input type="range" min={1} max={10} value={pointSize} onChange={e => setPointSize(+e.target.value)} className="w-20" />
              <span className="font-mono">{pointSize}</span>
            </label>
            <label className="flex items-center gap-1.5">
              <span className="opacity-60">Opacity</span>
              <input type="range" min={0.1} max={1} step={0.05} value={opacity} onChange={e => setOpacity(+e.target.value)} className="w-20" />
              <span className="font-mono">{Math.round(opacity * 100)}%</span>
            </label>
            <label className="flex items-center gap-1.5" data-testid="overlay-live-tolerance">
              <span className="opacity-60">Live tolerance</span>
              <input type="range" min={1} max={200} value={liveTolerance} onChange={e => setLiveTolerance(+e.target.value)} className="w-32" />
              <span className="font-mono">{liveTolerance} mm</span>
            </label>
            <button onClick={toggleSectionPlane}
              className="flex items-center gap-1 px-2 py-1 rounded ml-auto"
              style={{
                background: sectionPlaneOn ? '#10b98122' : '#0a0e14',
                color: sectionPlaneOn ? '#10b981' : '#9ca3af',
                border: '1px solid #1f2937',
              }}
              data-testid="overlay-section-btn">
              <Scissors className="w-3 h-3" /> {sectionPlaneOn ? 'Section ON' : 'Section'}
            </button>
            <button onClick={toggleMeasure}
              className="flex items-center gap-1 px-2 py-1 rounded"
              style={{
                background: measureOn ? '#3b82f622' : '#0a0e14',
                color: measureOn ? '#3b82f6' : '#9ca3af',
                border: '1px solid #1f2937',
              }}
              data-testid="overlay-measure-btn">
              <Ruler className="w-3 h-3" /> {measureOn ? 'Measure ON' : 'Measure'}
            </button>
            {selectedZoneId && (
              <button onClick={clearHighlight}
                className="flex items-center gap-1 px-2 py-1 rounded"
                style={{ background: '#3b82f622', color: '#3b82f6' }}>
                <X className="w-3 h-3" /> Clear zone
              </button>
            )}
          </div>
        </div>

        {/* Side panel: stats + zones */}
        <div className="xl:col-span-4 space-y-3">
          {stats && (
            <div className="rounded-xl p-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="overlay-stats">
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Statistics</div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <Stat k="Mean" v={`${stats.meanDeviationMm.toFixed(2)}mm`} />
                <Stat k="RMS" v={`${stats.rmsDeviationMm.toFixed(2)}mm`} />
                <Stat k="Max" v={`${stats.maxDeviationMm.toFixed(2)}mm`} color="#ef4444" />
                <Stat k="P95" v={`${stats.percentile95Mm.toFixed(2)}mm`} />
                <Stat k="Within Tol" v={`${stats.withinTolerancePct.toFixed(1)}%`} color="#10b981" />
                <Stat k="OOT pts" v={(stats.outOfToleranceCount || 0).toLocaleString()} color="#f59e0b" />
              </div>
            </div>
          )}

          {comparison && (
            <div className="rounded-xl p-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }}>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">Approval</div>
              <div className="text-xs mb-2">Status: <span className="font-semibold" style={{
                color: comparison.approval?.status === 'APPROVED' ? '#10b981' :
                       comparison.approval?.status === 'REJECTED' ? '#ef4444' :
                       comparison.approval?.status === 'SUBMITTED' ? '#3b82f6' : '#94a3b8',
              }}>{comparison.approval?.status || 'DRAFT'}</span></div>
              <div className="flex flex-wrap gap-1">
                {comparison.approval?.status !== 'SUBMITTED' && comparison.approval?.status !== 'APPROVED' && (
                  <button onClick={submitForApproval} className="flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                    style={{ background: '#3b82f622', color: '#3b82f6' }} data-testid="overlay-submit-btn">
                    <Send className="w-3 h-3" /> Submit
                  </button>
                )}
                {comparison.approval?.status === 'SUBMITTED' && (
                  <>
                    <button onClick={approve} className="flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                      style={{ background: '#10b98122', color: '#10b981' }} data-testid="overlay-approve-btn">
                      <ShieldCheck className="w-3 h-3" /> Approve
                    </button>
                    <button onClick={reject} className="flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                      style={{ background: '#ef444422', color: '#ef4444' }} data-testid="overlay-reject-btn">
                      <ShieldOff className="w-3 h-3" /> Reject
                    </button>
                  </>
                )}
                <button onClick={() => exportReport('pdf')} className="flex items-center gap-1 px-2 py-1 rounded text-[11px] ml-auto"
                  style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }} data-testid="overlay-export-pdf-btn">
                  <FileDown className="w-3 h-3" /> PDF
                </button>
                <button onClick={() => exportReport('pptx')} className="flex items-center gap-1 px-2 py-1 rounded text-[11px]"
                  style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }}>
                  <FileDown className="w-3 h-3" /> PPTX
                </button>
              </div>
            </div>
          )}

          <CameraBookmarksPanel projectId={projectId} comparisonId={comparisonId} viewerRef={viewerRef} />

          <div className="rounded-xl p-3" style={{ background: '#0f1722', border: '1px solid #1f2937' }} data-testid="overlay-zones-panel">
            <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1 flex items-center gap-1"><Crosshair className="w-3 h-3" /> Deviation Zones ({zones.length})</div>
            {zones.length === 0 && <div className="text-xs opacity-50 py-4 text-center">No zones. Execute a comparison.</div>}
            <div className="space-y-1.5 max-h-[440px] overflow-y-auto pr-1">
              {zones.map(z => (
                <div key={z.zoneId} className="rounded-lg p-2" style={{ background: '#0a0e14', border: `1px solid ${SEV_COLOR[z.severity] || '#1f2937'}44` }}
                  data-testid={`overlay-zone-${z.zoneId}`}>
                  <div className="flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-1.5">
                      <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold"
                        style={{ background: SEV_COLOR[z.severity] + '33', color: SEV_COLOR[z.severity] }}>
                        {z.severity}
                      </span>
                      <span className="font-mono opacity-80">{z.zoneId}</span>
                    </div>
                    <span className="font-semibold" style={{ color: SEV_COLOR[z.severity] }}>
                      {z.maxDeviationMm.toFixed(1)} mm
                    </span>
                  </div>
                  <div className="text-[10px] opacity-60 mt-0.5">
                    {z.direction} · {z.pointCount.toLocaleString()} pts · mean {z.meanDeviationMm.toFixed(1)} mm
                  </div>
                  <div className="flex items-center gap-1 mt-1.5">
                    <button onClick={() => flyTo(z)} className="px-2 py-0.5 rounded text-[10px]" style={{ background: '#3b82f6', color: '#fff' }}
                      data-testid={`overlay-flyto-${z.zoneId}`}>
                      <Target className="w-3 h-3 inline mr-0.5" /> Fly to
                    </button>
                    {!z.raidItemId ? (
                      <button onClick={() => raiseRAID(z)} className="px-2 py-0.5 rounded text-[10px]"
                        style={{ background: '#f59e0b', color: '#fff' }} data-testid={`overlay-raid-${z.zoneId}`}>
                        <AlertCircle className="w-3 h-3 inline mr-0.5" /> Raise RAID
                      </button>
                    ) : (
                      <a
                        href={`/raid?projectId=${projectId}&itemId=${encodeURIComponent(z.raidItemId)}`}
                        target="_blank" rel="noreferrer"
                        className="px-2 py-0.5 rounded text-[10px] hover:opacity-80"
                        style={{ background: '#ef444433', color: '#ef4444' }}
                        title={`Open RAID ${z.raidItemId} in a new tab`}
                        data-testid={`overlay-raid-open-${z.zoneId}`}>
                        <ExternalLink className="w-3 h-3 inline mr-0.5" />
                        {z.raidItemId}
                      </a>
                    )}
                    <label className="px-2 py-0.5 rounded text-[10px] cursor-pointer ml-auto" style={{ background: '#0f1722', color: '#9ca3af', border: '1px solid #1f2937' }}>
                      <Camera className="w-3 h-3 inline mr-0.5" /> +Photo
                      <input type="file" hidden accept="image/*" capture="environment"
                        onChange={e => attachPhoto(z, e.target.files?.[0])} />
                    </label>
                  </div>
                  {z.photos?.length > 0 && (
                    <div className="flex gap-1 mt-1.5">
                      {z.photos.slice(0, 4).map(p => (
                        <img key={p.filename} src={(process.env.REACT_APP_BACKEND_URL || '') + p.url}
                          alt="" className="w-10 h-10 object-cover rounded" />
                      ))}
                      {z.photos.length > 4 && <span className="text-[10px] opacity-50 self-center">+{z.photos.length - 4}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v, color }) {
  return (
    <div className="rounded p-1.5" style={{ background: '#111827' }}>
      <div className="opacity-50 text-[9px] uppercase">{k}</div>
      <div className="font-semibold" style={color ? { color } : null}>{v}</div>
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider opacity-80" data-testid="overlay-legend">
      <span className="opacity-60">Deviation:</span>
      {[
        { c: '#10b981', l: 'Within 20% tol' },
        { c: '#eab308', l: '20–60%' },
        { c: '#f97316', l: '60–100%' },
        { c: '#ef4444', l: '> tolerance' },
      ].map(s => (
        <span key={s.l} className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.c }} /> {s.l}
        </span>
      ))}
    </div>
  );
}
